import type { DocumentType, PeopleData, RoutingConfig } from '@lg/shared';

/**
 * Approval routing simulator and coverage check (spec 10.3, 10.4). Pure. It
 * mirrors the configured D365 workflow rules; the sandbox smoke run confirms
 * real behaviour.
 */

export interface RoutingHolder {
  name: string;
  upn: string | null;
  personnelNumber: string;
}

export interface RoutingPosition {
  positionId: string;
  title: string;
  jobId: string;
  reportsToPositionId: string | null;
  holder: RoutingHolder | null;
  /** True when the position lives in D365 rather than the import (regional / corporate). */
  external: boolean;
}

export interface RoutingPeople {
  positions: ReadonlyMap<string, RoutingPosition>;
  /** submitter UPN → primary position ID */
  primaryPositionByUpn: ReadonlyMap<string, string>;
  /** `${jobId}|${documentType}` → limit (USD) */
  limits: ReadonlyMap<string, number>;
  /** roleId → users holding it in the company */
  roleHolders: ReadonlyMap<string, Array<{ name: string; upn: string }>>;
}

export interface Approver {
  name: string;
  upn: string | null;
  positionId?: string;
  title?: string;
  limitUsd?: number;
}

export interface ApproverStep {
  label: string;
  kind: 'managerial' | 'role' | 'escalation';
  approvers: Approver[];
}

export interface RoutingGap {
  label: string;
  message: string;
}

export interface RoutingResult {
  chain: ApproverStep[];
  gaps: RoutingGap[];
  warnings: string[];
}

export interface RoutingInput {
  submitterUpn: string;
  documentType: DocumentType;
  amountUsd: number;
}

export const limitKey = (jobId: string, documentType: DocumentType): string => `${jobId}|${documentType}`;

const usd = (n: number): string => `$${n.toLocaleString('en-US')}`;

export function simulateRouting(
  input: RoutingInput,
  people: RoutingPeople,
  config: RoutingConfig,
): RoutingResult {
  const docConfig = config.documentTypes.find((d) => d.type === input.documentType);
  const result: RoutingResult = { chain: [], gaps: [], warnings: [] };
  if (!docConfig) {
    result.gaps.push({ label: input.documentType, message: 'No routing configured for this document type' });
    return result;
  }
  if (!docConfig.inScope) {
    result.warnings.push(`${input.documentType} is not in scope for this template`);
    return result;
  }

  const escalate = (label: string, reason: string): void => {
    const holders = people.roleHolders.get(docConfig.escalationRoleId) ?? [];
    if (holders.length === 0) {
      result.gaps.push({
        label,
        message: `${reason} and no user holds escalation role ${docConfig.escalationRoleId}`,
      });
      return;
    }
    result.warnings.push(`${reason}; escalated to ${docConfig.escalationRoleId}`);
    result.chain.push({
      label: `Escalation (${docConfig.escalationRoleId})`,
      kind: 'escalation',
      approvers: holders.map((h) => ({ name: h.name, upn: h.upn })),
    });
  };

  for (const step of docConfig.steps) {
    if (step.kind === 'role') {
      const holders = people.roleHolders.get(step.roleId) ?? [];
      if (holders.length === 0) {
        result.gaps.push({ label: step.label, message: `No user holds role ${step.roleId} in this company` });
      } else {
        result.chain.push({
          label: step.label,
          kind: 'role',
          approvers: holders.map((h) => ({ name: h.name, upn: h.upn })),
        });
      }
      continue;
    }

    // Managerial hierarchy: start above the submitter's primary position.
    const start = people.primaryPositionByUpn.get(input.submitterUpn.toLowerCase());
    if (!start) {
      result.gaps.push({ label: step.label, message: `${input.submitterUpn} has no primary position` });
      continue;
    }
    const retrieved: Approver[] = [];
    const visited = new Set<string>([start]);
    let current = people.positions.get(start)?.reportsToPositionId ?? null;
    let covered = false;
    while (current) {
      if (visited.has(current)) {
        result.warnings.push(`Reporting line loops at ${current}`);
        break;
      }
      visited.add(current);
      const pos = people.positions.get(current);
      if (!pos) {
        result.warnings.push(`Position ${current} was not found; the hierarchy stops there`);
        break;
      }
      if (!pos.holder) {
        result.warnings.push(`Position ${pos.positionId} (${pos.title}) is vacant and was skipped`);
      } else {
        const limit = people.limits.get(limitKey(pos.jobId, input.documentType)) ?? 0;
        retrieved.push({
          name: pos.holder.name,
          upn: pos.holder.upn,
          positionId: pos.positionId,
          title: pos.title,
          limitUsd: limit,
        });
        if (limit >= input.amountUsd) {
          covered = true;
          break;
        }
      }
      current = pos.reportsToPositionId;
    }

    if (covered) {
      const last = retrieved[retrieved.length - 1];
      const approvers = step.assign === 'allRetrieved' ? retrieved : last ? [last] : [];
      result.chain.push({ label: step.label, kind: 'managerial', approvers });
    } else {
      escalate(step.label, `No one in the hierarchy can approve ${usd(input.amountUsd)}`);
    }
  }
  return result;
}

// ── Coverage (rule P8) ───────────────────────────────────────────────────────

export interface CoverageCell {
  amountUsd: number;
  status: 'ok' | 'warning' | 'gap';
  /** First failing case, for drill-down into the routing preview. */
  example: { submitterUpn: string; message: string } | null;
}

export interface CoverageMatrix {
  amounts: number[];
  rows: Array<{ documentType: DocumentType; inScope: boolean; cells: CoverageCell[] }>;
  gapCount: number;
}

/** Test amounts: 1, each distinct non-zero limit, each limit + 1. */
export function coverageAmounts(limits: PeopleData['approvalLimits']): number[] {
  const set = new Set<number>([1]);
  for (const l of limits) {
    if (l.amountUsd > 0) {
      set.add(l.amountUsd);
      set.add(l.amountUsd + 1);
    }
  }
  return [...set].sort((a, b) => a - b);
}

export function computeCoverage(
  people: RoutingPeople,
  config: RoutingConfig,
  submitters: readonly string[],
  amounts: readonly number[],
): CoverageMatrix {
  let gapCount = 0;
  const rows = config.documentTypes.map((doc) => {
    const cells: CoverageCell[] = amounts.map((amountUsd) => {
      if (!doc.inScope) return { amountUsd, status: 'ok', example: null };
      let status: CoverageCell['status'] = 'ok';
      let example: CoverageCell['example'] = null;
      for (const submitterUpn of submitters) {
        const r = simulateRouting({ submitterUpn, documentType: doc.type, amountUsd }, people, config);
        const gap = r.gaps[0];
        if (gap) {
          status = 'gap';
          example = { submitterUpn, message: gap.message };
          break;
        }
        const vacancy = r.warnings.find((w) => w.includes('vacant'));
        if (vacancy && status === 'ok') {
          status = 'warning';
          example = { submitterUpn, message: vacancy };
        }
      }
      if (status === 'gap') gapCount += 1;
      return { amountUsd, status, example };
    });
    return { documentType: doc.type, inScope: doc.inScope, cells };
  });
  return { amounts: [...amounts], rows, gapCount };
}
